import { useEffect, useState } from "react";

import { FormProvider } from "react-hook-form";

import Box from "@/components/Box";
import DutchTestQuestionsField, {
  shouldShowDutchTestQuestions
} from "@/components/DutchTestQuestions";
import Grid from "@/components/Grid";
import BaseProductQtyInput from "@/components/ProductQtyInput";
import { useOrganisationBasketContext } from "@/contexts/organisations/OrganisationBasketContext";
import { useOrganisationContext } from "@/contexts/organisations/OrganisationContext";
import {
  BASKET_INVALID_RESTRICTED_SHIPPING,
  BASKET_INVALID_RESTRICTED_ORGANISATION,
  BASKET_INVALID_RESTRICTED_PRACTITIONER,
  BASKET_INVALID_NO_PATIENT_SELECTED_WHEN_MULTIPLE_PATIENTS_ENABLED,
  BASKET_INVALID_PRODUCT_NOT_AVAILABLE,
  BASKET_INVALID_OVERLAPPING_CYREX_PRODUCTS,
  BASKET_INVALID_CYREX_SAMPLE,
  BASKET_INVALID_CYREX_MISSING_STORED_SAMPLE,
  BASKET_INVALID_CYREX_MISSING_ADDON,
  BASKET_INVALID_CYREX_ADDON_DUPLICATE_SIBLING,
  BASKET_INVALID_CYREX_ADDON_MATCHES_BASE_TEST,
  BASKET_INVALID_CYREX_ADDON_ALREADY_ON_SAMPLE,
  BASKET_INVALID_CYREX_STORED_SAMPLE_STOCK_BASKET,
  BASKET_INVALID_CYREX_STORED_SAMPLE_MIXED_BASKET,
  BASKET_INVALID_CYREX_BUNDLE_STOCK_BASKET,
  BASKET_INVALID_CYREX_MULTIPLE_PATIENT_BASKET,
  BASKET_INVALID_DNA_TEST_REQUIRES_REPORT,
} from "@/core/constants";
import {
  buildInvalidCyrexSampleDetailMessage,
  buildOverlappingCyrexBasketDetailMessage
} from "../utils/cyrexBasketLineMessages";
import { ANSWER_DUTCH_TEST_RELATED_QUESTIONS_MUTATION } from "@/graphql/accounts";
import useDjangoGraphqlForm from "@/hooks/form/useDjangoGraphqlForm";
import Badge from "@/tpo/Badge";
import Currency from "@/tpo/Currency";
import Group from "@/tpo/Group";
import { Error } from "@/tpo/InputWrapper";
import Stack from "@/tpo/Stack";
import ButtonV2 from "@/v2/Buttons";

import CyrexStoredSampleSummary from "../../shop/ui/CyrexStoredSampleSummary";

import classes from "./BasketItem.module.css";
import SelectPatientPerLine from "./SelectPatientPerLine";

/** Hide £0 / null amounts in basket line UI (same pattern as prac shop price display). */
function shouldDisplayBasketPrice(value) {
  return value != null && Number(value) !== 0;
}

function ProductQtyInput({ count: initialCount, disabled, setQty }) {
  const [count, setCount] = useState(initialCount);

  return (
    <BaseProductQtyInput
      value={count}
      disabled={disabled}
      onChange={n => {
        setCount(n);
      }}
      onBlur={() => {
        if (count !== "") {
          if (count !== initialCount) {
            setQty(count);
          }
        } else {
          setCount(initialCount);
        }
      }}
    />
  );
}

function LineError({ error, basketLineItem, addonLine }) {
  const { basket } = useOrganisationBasketContext();

  let errorMessage;

  if (error === BASKET_INVALID_OVERLAPPING_CYREX_PRODUCTS && basketLineItem) {
    errorMessage = buildOverlappingCyrexBasketDetailMessage(basketLineItem, addonLine || null);
  }

  if (error === BASKET_INVALID_CYREX_SAMPLE && basketLineItem) {
    errorMessage = buildInvalidCyrexSampleDetailMessage(basketLineItem);
  }

  if (error === BASKET_INVALID_RESTRICTED_SHIPPING && basket?.shippingAddress?.country?.name) {
    errorMessage = `This product is not available in ${basket?.shippingAddress?.country?.name} please remove to continue `;
  }

  if (error === BASKET_INVALID_RESTRICTED_ORGANISATION) {
    errorMessage = "This product is not permitted to be sold to your organisation";
  }

  if (error === BASKET_INVALID_RESTRICTED_PRACTITIONER) {
    errorMessage = "You are not authorised to purchase this product";
  }

  if (error === BASKET_INVALID_NO_PATIENT_SELECTED_WHEN_MULTIPLE_PATIENTS_ENABLED) {
    errorMessage = "Please select a patient to continue";
  }

  if (error === BASKET_INVALID_PRODUCT_NOT_AVAILABLE) {
    errorMessage = "This product is currently unavailable";
  }

  if (error === BASKET_INVALID_CYREX_MISSING_STORED_SAMPLE) {
    errorMessage =
      "Select a patient with a valid stored sample, or choose a different Cyrex purchase option.";
  }

  if (error === BASKET_INVALID_CYREX_MISSING_ADDON) {
    errorMessage =
      "Cyrex base tests require at least one array.";
  }

  if (error === BASKET_INVALID_CYREX_ADDON_DUPLICATE_SIBLING) {
    errorMessage =
      "This array is already added to the base test. Remove the duplicate add-on to continue.";
  }

  if (error === BASKET_INVALID_CYREX_ADDON_MATCHES_BASE_TEST) {
    errorMessage =
      "This array matches the base test and cannot be added as an add-on. Remove it to continue.";
  }

  if (error === BASKET_INVALID_CYREX_ADDON_ALREADY_ON_SAMPLE) {
    errorMessage =
      "This array is already included on the linked sample. Remove the duplicate add-on to continue.";
  }

  if (error === BASKET_INVALID_CYREX_STORED_SAMPLE_STOCK_BASKET) {
    errorMessage =
      "Cyrex stored sample retrieval cannot be used with stock orders. To continue, select Blood Serum Kit as the test option or disable Order as stock.";
  }

  if (error === BASKET_INVALID_CYREX_STORED_SAMPLE_MIXED_BASKET) {
    errorMessage =
      "Stored sample retrieval must be ordered separately. Remove other items from the basket or place the stored sample retrieval in a separate order.";
  }

  if (error === BASKET_INVALID_CYREX_BUNDLE_STOCK_BASKET) {
    errorMessage =
      "Cyrex bundle tests cannot be purchased as stock. Remove the bundle from your basket or disable Order as stock.";
  }

  if (error === BASKET_INVALID_CYREX_MULTIPLE_PATIENT_BASKET) {
    errorMessage =
      "Cyrex tests cannot be included in a multi-patient order. Remove Cyrex products or switch to a single-patient basket.";
  }

  if (error === BASKET_INVALID_OVERLAPPING_CYREX_PRODUCTS && !errorMessage) {
    errorMessage =
      "Overlapping Cyrex arrays are in conflict. Remove overlapping add-ons from your basket, or remove add-ons that clash with the linked stored sample.";
  }

  if (error === BASKET_INVALID_CYREX_SAMPLE && !errorMessage) {
    errorMessage =
      "The stored sample linked to this line is not valid for Cyrex add-on orders. Update the sample or remove add-ons to continue.";
  }

  if (error === BASKET_INVALID_DNA_TEST_REQUIRES_REPORT) {
    errorMessage = "Please add at least one report to continue";
  }

  if (errorMessage) {
    return <Error fontSize={[16, 16, 18]} error={errorMessage} />;
  }

  return null;
}

function UpdateDutchTestRelatedQuestionAnswers({
  id,
  birthControl,
  postMenopausal,
  hrt
}) {
  const { organisation } = useOrganisationContext();

  const { methods } = useDjangoGraphqlForm({
    mutation: ANSWER_DUTCH_TEST_RELATED_QUESTIONS_MUTATION,
    mutationName: "answerDutchTestRelatedQuestionsMutation",
    defaultValues: {
      id,
      birthControl,
      postMenopausal,
      hrt
    },
    transformer: input => ({
      ...input,
      organisation: parseInt(organisation.id)
    }),
    handleSuccess: ({ data, api: { reset } }) => {
      const patientProfile = data.answerDutchTestRelatedQuestionsMutation.patientProfile;
      reset({
        id: patientProfile.pk,
        birthControl: patientProfile.birthControl,
        postMenopausal: patientProfile.postMenopausal,
        hrt: patientProfile.hrt
      });
    },
    onChange: (values, { onSubmit }) => {
      onSubmit(values);
    }
  });

  const { reset } = methods;

  useEffect(() => {
    if (id) {
      reset({
        id,
        birthControl,
        postMenopausal,
        hrt
      });
    }
  }, [id, birthControl, postMenopausal, hrt, reset]);

  return (
    <FormProvider {...methods}>
      <DutchTestQuestionsField />
    </FormProvider>
  );
}

function BasketItemUI({
  error,
  basketLineItem,
  count: initialCount,
  name,
  subname,
  symbol,
  price,
  previousPrice,
  rrpPrice,
  setQty,
  removeItemFromBasket,
  addons,
  onClick,
  "data-component-name": dataComponentName = "BasketItemUI",
  className,
  py
}) {
  const { removeAddOnFromProduct, loading } = useOrganisationBasketContext();

  return (
    <Grid
      className={className}
      gridTemplateColumns="auto 1fr"
      data-component-name={dataComponentName}
      gridRowGap={["10px", "10px", "20px"]}
      style={{
        gridColumnGap: 10
      }}
      py={py}
    >
      <Group gap={10}>
        {setQty ? (
          <ProductQtyInput
            key={initialCount}
            count={initialCount}
            disabled={loading}
            setQty={setQty}
          />
        ) : null}
        <Group
          alignItems="flex-start"
          justifyContent="space-between"
          flexGrow={1}
          flexDirection="column"
          m="auto"
        >
          <Box
            className={classes.BasketItem}
            onClick={onClick}
            fontFamily="gilroyBold"
            fontSize={[16, 16, 18]}
            data-testid="basket-item-name"
          >
            {name}
          </Box>
          {subname && (
            <Box
              fontFamily="gilroyMedium"
              fontSize={[14, 14, 16]}
              color="grey"
              data-testid="basket-item-subname"
            >
              {subname}
            </Box>
          )}
          <ButtonV2
            mt={1}
            letterSpacing="unset"
            fontSize={16}
            textTransform="none"
            variant="link"
            onClick={
              setQty
                ? () => {
                    setQty(0);
                  }
                : removeItemFromBasket
            }
            disabled={loading}
            fontFamily="gilroyRegular"
            data-component-name="RemoveButton"
            size={null}
          >
            Remove
          </ButtonV2>
        </Group>
      </Group>
      <Group
        alignItems="flex-start"
        gap={10}
        justifyContent="center"
        flexGrow={1}
        gridRowStart={["2", "1"]}
        gridRowEnd={["3", "1"]}
        gridColumnStart={["1", "2"]}
        gridColumnEnd={["3"]}
      >
        <Group
          alignItems="flex-end"
          justifyContent="space-between"
          flexGrow={1}
          flexDirection="column"
          style={{ minWidth: "80px" }}
        >
          <Group alignItems="flex-start" gap={4} justifyContent="space-between" flexGrow={1} my={1}>
            {shouldDisplayBasketPrice(price) &&
              shouldDisplayBasketPrice(previousPrice) &&
              previousPrice > price && (
                <Currency
                  symbol={symbol}
                  fontSize={28}
                  value={previousPrice}
                  color="black"
                  strikethrough
                  data-testid="basket-item-previous-price"
                />
              )}
            {shouldDisplayBasketPrice(price) ? (
              <Currency
                symbol={symbol}
                fontSize={28}
                value={price}
                color={
                  shouldDisplayBasketPrice(previousPrice) && previousPrice > price
                    ? "red"
                    : "black"
                }
                data-testid="basket-item-price"
              />
            ) : null}
          </Group>
          {shouldDisplayBasketPrice(rrpPrice) ? (
            <Currency
              symbol={symbol}
              fontSize={18}
              value={rrpPrice}
              prefix="RRP"
              color="midGrey"
              strikethrough
              data-testid="basket-item-rrpprice"
            />
          ) : null}
        </Group>
      </Group>
      {!!error && <LineError error={error} basketLineItem={basketLineItem} />}
      {!!addons?.length && (
        <Stack
          gap={10}
          gridColumnStart={["1"]}
          gridColumnEnd={["3"]}
          data-component-name="BasketItemUiAddons"
          mt={2}
        >
          {addons.map(addon => (
            <Stack key={addon.key}>
              <Group
                gap={10}
                justifyContent="space-between"
                alignItems="center"
                data-component-name="BasketItemUiAddon"
              >
                <Group alignItems="center" gap={10} flexWrap="wrap">
                  <Badge bg="dark" color="white" size="md" fontSize={10}>
                    add-on
                  </Badge>
                  {!addon.isRequiredAddon && (
                    <ButtonV2
                      color="red"
                      size="xs"
                      type="button"
                      onClick={() => {
                        removeAddOnFromProduct(addon.parentProduct.id, addon.product.id);
                      }}
                    >
                      remove
                    </ButtonV2>
                  )}
                  <Box fontFamily="gilroyBold" fontSize={[16]} data-testid="BasketItemUiAddon:name">
                    {addon.name}
                  </Box>
                </Group>
                {addon.isRequiredAddon ? null : addon.discountValue && addon.discountValue > 0 ? (
                  shouldDisplayBasketPrice(addon.price) ||
                  shouldDisplayBasketPrice(addon.price - addon.discountValue) ? (
                    <Group alignItems="center" gap={4}>
                      {shouldDisplayBasketPrice(addon.price) ? (
                        <Currency
                          symbol={symbol}
                          fontSize={18}
                          value={addon.price}
                          color="black"
                          strikethrough
                          data-testid="BasketItemUiAddon:previous-price"
                        />
                      ) : null}
                      {shouldDisplayBasketPrice(addon.price - addon.discountValue) ? (
                        <Currency
                          symbol={symbol}
                          value={addon.price - addon.discountValue}
                          fontSize={18}
                          color="red"
                          data-testid="BasketItemUiAddon:price"
                        />
                      ) : null}
                    </Group>
                  ) : null
                ) : shouldDisplayBasketPrice(addon.price) ? (
                  <Currency
                    symbol={symbol}
                    value={addon.price}
                    fontSize={18}
                    data-testid="BasketItemUiAddon:price"
                  />
                ) : null}
              </Group>
              {!!addon.invalidReason && (
                <LineError
                  error={addon.invalidReason}
                  basketLineItem={basketLineItem}
                  addonLine={addon}
                />
              )}
            </Stack>
          ))}
        </Stack>
      )}
      {basketLineItem?.originalSampleOrderTestItem ? (
        <Box
          gridColumnStart={["1"]}
          gridColumnEnd={["3"]}
          mt={2}
          width="100%"
          data-component-name="BasketItemUiOriginalSampleOrderTestItem"
        >
          <CyrexStoredSampleSummary
            sample={basketLineItem.originalSampleOrderTestItem}
            panel
          />
        </Box>
      ) : null}
    </Grid>
  );
}

export function BasketItem({
  error,
  basketLineItem,
  count,
  name,
  subname,
  symbol,
  price,
  previousPrice,
  rrpPrice,
  discountValue,
  clinicName,
  address,
  fees,
  setQty,
  removeItemFromBasket,
  addons,
  onClick,
  id,
  patient, // line item patient
  showSelectPatientPerLine = false,
  showDutchTestQuestions = false
}) {
  const { basket } = useOrganisationBasketContext();

  let dutchTestRelatedForm = null;

  if (basket?.multiplePatientsEnabled) {
    if (patient && shouldShowDutchTestQuestions(patient.gender)) {
      dutchTestRelatedForm = (
        <UpdateDutchTestRelatedQuestionAnswers
          id={patient.pk}
          dateOfBirth={patient.dateOfBirth}
          birthControl={patient.birthControl}
          postMenopausal={patient.postMenopausal}
          hrt={patient.hrt}
          key="patient"
        />
      );
    }
  } else {
    if (basket?.patient && shouldShowDutchTestQuestions(basket.patient.gender)) {
      dutchTestRelatedForm = (
        <UpdateDutchTestRelatedQuestionAnswers
          id={basket.patient.pk}
          dateOfBirth={basket.patient.dateOfBirth}
          birthControl={basket.patient.birthControl}
          postMenopausal={basket.patient.postMenopausal}
          hrt={basket.patient.hrt}
          key="basket"
        />
      );
    }
  }

  if (discountValue && discountValue > 0) {
    previousPrice = price;
    price = price - discountValue;
  }

  if (clinicName) {
    return (
      <Stack className="BasketItemUI" gap={20} data-component-name="BasketItemUI" py={20}>
        <BasketItemUI
          error={error}
          basketLineItem={basketLineItem}
          count={count}
          name={name}
          subname={subname}
          symbol={symbol}
          price={price}
          previousPrice={previousPrice}
          setQty={setQty}
          removeItemFromBasket={removeItemFromBasket}
          addons={addons}
          onClick={onClick}
          data-component-name={null}
        />
        <Box>
          <Box fontFamily="gilroyBold" fontSize={16}>
            {clinicName}
          </Box>
          <Box fontFamily="gilroyMedium" fontSize={14}>
            {address}
          </Box>
          {shouldDisplayBasketPrice(fees) ? (
            <Group alignItems="center" justifyContent="space-between">
              <Box fontFamily="gilroyBold" fontSize={16}>
                Clinic fee
              </Box>
              <Currency symbol={symbol} fontSize={18} value={fees} data-testid="clinicFee" />
            </Group>
          ) : null}
        </Box>
        {showSelectPatientPerLine && (
          <SelectPatientPerLine patient={patient} basketLineItemId={id} />
        )}
        {showDutchTestQuestions && dutchTestRelatedForm}
      </Stack>
    );
  } else {
    return (
      <Stack className="BasketItemUI">
        <BasketItemUI
          error={error}
          basketLineItem={basketLineItem}
          count={count}
          name={name}
          subname={subname}
          symbol={symbol}
          price={price}
          rrpPrice={rrpPrice}
          previousPrice={previousPrice}
          setQty={setQty}
          removeItemFromBasket={removeItemFromBasket}
          addons={addons}
          onClick={onClick}
          py={20}
        />
        {showSelectPatientPerLine && (
          <SelectPatientPerLine patient={patient} basketLineItemId={id} />
        )}
        {showDutchTestQuestions && dutchTestRelatedForm}
      </Stack>
    );
  }
}
